import React, { useState, useEffect } from "react";
import { AlertTriangle, X } from "lucide-react";

/**
 * SaveConfirmDialog — modal shown before saving a quote.
 *
 * Three estimator-confirmation tick-boxes (all must be ticked before the
 * Save button enables) plus a free-text warning about complex jobs needing a
 * technical review. Mirrors a pre-flight check that prevents quotes going
 * out the door with missing parts, consumables, or scope gaps.
 *
 * Tick state intentionally resets every time the dialog opens — the
 * estimator must actively re-confirm on every save, not "tick once, save
 * many times".
 */
export function SaveConfirmDialog({ open, onCancel, onConfirm, isEdit = false }) {
  const [c1, setC1] = useState(false);
  const [c2, setC2] = useState(false);
  const [c3, setC3] = useState(false);

  useEffect(() => {
    if (open) { setC1(false); setC2(false); setC3(false); }
  }, [open]);

  if (!open) return null;
  const allTicked = c1 && c2 && c3;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 pt-8 sm:pt-16 bg-slate-900/60 backdrop-blur-sm overflow-y-auto" data-testid="save-confirm-dialog">
      <div className="bg-white max-w-lg w-full shadow-2xl border border-slate-200 rounded-lg overflow-hidden">
        <div className="flex items-start justify-between px-6 py-4 border-b border-slate-200 bg-amber-50">
          <div className="flex items-start gap-3">
            <AlertTriangle size={22} className="text-amber-600 mt-0.5 shrink-0" />
            <div>
              <h2 className="text-base font-semibold text-slate-900">Confirm before saving</h2>
              <p className="text-xs text-slate-600 mt-0.5">By saving this quote you confirm the below is correct. If this is a complex quote you may need to get someone technical to review.</p>
            </div>
          </div>
          <button onClick={onCancel} className="text-slate-400 hover:text-slate-700" data-testid="save-confirm-close">
            <X size={18} />
          </button>
        </div>

        <div className="px-6 py-5 space-y-3">
          <label className="flex items-start gap-3 cursor-pointer hover:bg-slate-50 p-2 -mx-2 rounded">
            <input
              type="checkbox"
              checked={c1}
              onChange={(e) => setC1(e.target.checked)}
              className="mt-1 h-4 w-4"
              data-testid="save-confirm-check-parts"
            />
            <span className="text-sm text-slate-800">Have you considered all parts required?</span>
          </label>
          <label className="flex items-start gap-3 cursor-pointer hover:bg-slate-50 p-2 -mx-2 rounded">
            <input
              type="checkbox"
              checked={c2}
              onChange={(e) => setC2(e.target.checked)}
              className="mt-1 h-4 w-4"
              data-testid="save-confirm-check-consumables"
            />
            <span className="text-sm text-slate-800">Have you considered all consumables?</span>
          </label>
          <label className="flex items-start gap-3 cursor-pointer hover:bg-slate-50 p-2 -mx-2 rounded">
            <input
              type="checkbox"
              checked={c3}
              onChange={(e) => setC3(e.target.checked)}
              className="mt-1 h-4 w-4"
              data-testid="save-confirm-check-description"
            />
            <span className="text-sm text-slate-800">Does the Job Description cover all aspects of the works quoted?</span>
          </label>
        </div>

        <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 flex items-center justify-end gap-2">
          <button
            onClick={onCancel}
            className="h-9 px-4 text-sm text-slate-700 hover:bg-slate-200 rounded"
            data-testid="save-confirm-cancel"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={!allTicked}
            className="h-9 px-5 bg-brand-blue text-white text-xs uppercase tracking-wider font-semibold rounded hover:bg-brand-blue-dark disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="save-confirm-confirm"
          >
            {isEdit ? "Update Quote" : "Save Quote"}
          </button>
        </div>
      </div>
    </div>
  );
}
